import * as Sentry from '@sentry/react';
import { getNetworkInfo } from '.';

/**
 * Utility function to play a media element after setting its source.
 *
 * @param mediaElement - The HTMLMediaElement to play.
 * @param url - The media source URL to be set on the element.
 * @returns A promise that resolves when playback starts successfully, or rejects with an error.
 */
export const playMediaElement = async (mediaElement: HTMLMediaElement, url: string): Promise<{ duration?: number }> => {
  return new Promise<{ duration?: number }>((resolve, reject) => {
    mediaElement.src = url;

    const onLoadedMetadata = () => {
      const duration = mediaElement.duration;
      mediaElement
        .play()
        .then(() => resolve({ duration }))
        .catch(reject);
      mediaElement.removeEventListener('loadedmetadata', onLoadedMetadata);
      mediaElement.removeEventListener('error', onError);
    };

    const onError = (error: Event) => {
      mediaElement.removeEventListener('loadedmetadata', onLoadedMetadata);
      mediaElement.removeEventListener('error', onError);
      mediaElement.removeEventListener('waiting', onBuffering);
      console.error('Media Play error', error);
      Sentry.captureMessage('Media Play Error', {
        level: 'error',
        tags: { category: 'media' },
        extra: {
          url,
          currentTime: mediaElement.currentTime,
          readyState: mediaElement.readyState,
          netWork: getNetworkInfo(),
        },
      });
      reject(new Error(`Media playback failed: ${error}`));
    };

    const onBuffering = () => {
      console.warn('Media Play Buffering');
      Sentry.captureMessage('Buffering started', {
        level: 'warning',
        tags: { category: 'media' },
        extra: {
          url,
          currentTime: mediaElement.currentTime,
          readyState: mediaElement.readyState,
          netWork: getNetworkInfo(),
        },
      });
    };

    mediaElement.addEventListener('loadedmetadata', onLoadedMetadata);
    mediaElement.addEventListener('error', onError);
    mediaElement.addEventListener('waiting', onBuffering);

    mediaElement.addEventListener('ended', () => {
      mediaElement.removeEventListener('waiting', onBuffering);
    });
  });
};
